import { productWhatsAppMessage, whatsappAriaLabel, whatsappLink } from '@sbg/shared/utils';
import { Button } from '@/components/ui/Button';
import type { ButtonSize } from '@/components/ui/button-classes';
import { WhatsAppGlyph } from '@/components/ui/glyphs';
import { track } from '@/lib/analytics';
import { absoluteUrl } from '@/lib/config';
import { useT } from '@/lib/i18n';
import { usePublicSettings } from '@/lib/site/public-settings';

export interface WhatsAppButtonProps {
  /** The product the chat is about — its name, SKU and page URL are pre-filled into the message. */
  product?: { name: string; sku: string; slug: string; variantId?: string };
  /**
   * A ready message for a page that is about something else than one stone (the contact page's own
   * greeting, for example). Ignored when `product` is given; the footer's general greeting is the default.
   */
  message?: string;
  size?: ButtonSize;
  className?: string;
  /** Show the words "Chat on WhatsApp" beside the glyph (the product page panel, D-362). */
  labelled?: boolean;
}

/**
 * Round WhatsApp button in the brand green (`#25D366`, official glyph). Opens
 * `https://wa.me/<number>?text=…` in a new tab with `rel="noopener noreferrer"`. The number is the
 * single `contact.whatsappNumber` site setting — it is never written into a component — and the
 * button renders nothing until one is configured.
 */
export function WhatsAppButton({
  product,
  message: pageMessage,
  size,
  className,
  labelled = false,
}: WhatsAppButtonProps) {
  const { whatsappNumber } = usePublicSettings();
  const t = useT();
  if (!whatsappNumber) return null;

  const message = product
    ? productWhatsAppMessage({
        name: product.name,
        sku: product.sku,
        url: absoluteUrl(
          `/shop/${product.slug}${product.variantId ? `?variant=${product.variantId}` : ''}`,
        ),
      })
    : (pageMessage ?? t('footer.whatsappGreeting'));
  const label = product ? whatsappAriaLabel(product.name) : t('footer.chatOnWhatsApp');

  return (
    <Button
      asChild
      variant="whatsapp"
      aria-label={label}
      {...(size ? { size } : {})}
      {...(className ? { className } : {})}
    >
      <a
        href={whatsappLink(whatsappNumber, message)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          // analytics (consent-gated, a closed event list): the stone's slug at most, never the message
          track('whatsapp_click', product ? { product: product.slug } : {});
        }}
      >
        <WhatsAppGlyph />
        {labelled ? <span>{t('footer.chatOnWhatsApp')}</span> : null}
      </a>
    </Button>
  );
}
